/**
 * The article status vocabulary, drawn.
 *
 * SEVEN STATUSES AND NO MORE. `ArticleStatus` is exactly
 * [draft, in_review, changes_requested, scheduled, published, unpublished, archived].
 * `manual_review` IS NOT ONE OF THEM — it is a work-queue state and never an article status — so
 * it is not in the map below and cannot be added without failing to compile.
 *
 * COLOUR IS NEVER THE ONLY SIGNAL. Each pill carries its own words; the tint is a second channel
 * for people who read colour quickly, not the channel. Every border in this palette measures
 * between 1.04:1 and 2.97:1 against a 3:1 requirement — a KNOWN, RULED contrast debt taken from
 * the live website (docs/ui/01-tokens.md, Q11/Q12, 2026-08-26) — which is exactly why nothing here
 * depends on the tint being distinguishable.
 */
import { Badge, Tooltip } from '@mantine/core';
import { i18n, useTranslation } from '../../../i18n';
import type { ArticleStatus } from '../api';

/** A brand token name, wrapped at the point of use. Each status's words are `cabinet:status.*`. */
type StatusTone = 'accent' | 'success' | 'warning' | 'danger' | 'muted' | 'info';

const STATUS_TONE: Readonly<Record<ArticleStatus, StatusTone>> = {
  draft: 'muted',
  in_review: 'info',
  changes_requested: 'warning',
  scheduled: 'accent',
  published: 'success',
  unpublished: 'danger',
  archived: 'muted',
};

const TONE_COLOUR: Readonly<Record<StatusTone, string>> = {
  accent: '--mx-accent',
  success: '--mx-success',
  warning: '--mx-warning',
  danger: '--mx-danger',
  info: '--mx-info',
  // ★ `m3` AND NOT `m6`, AND THE RULING NAMES THIS EXACT REMEDY. `docs/ui/01-tokens.md`:
  // "In the dark theme m6 through m9 are not usable for text on any surface" - and the sentence
  // after it offers two ways out, "a real retune of those two rungs, OR A CHANGE OF WHICH RUNG
  // EACH USE PICKS". This is the second. Measured 2026-09-23: the leg reported `#76706c` on
  // `#121110` at 3.86 against a ruled 4.5, three times, and m6 is the ink in every one.
  // ⚠ NOTHING ELSE IS WAITING. That document's "`m6` 58 times and `m7` 15 times" (:195) counts
  // `var(--m6)` in the PROTOTYPE's cabinet (:198-199), a name our code never writes. Measured over
  // web/{authors,admin,ui}/src, no text names `--mx-m6` or `--mx-m7`; muted text is `c="dimmed"`.
  muted: '--mx-m3',
};

/**
 * THE WORDS' OWN INK, WHERE A TONE CANNOT BE TEXT ON A TINT OF ITSELF. Light `warning` cannot:
 * 4.09 on this pill. `--mx-warning-ink` is web/ui's per-scheme alias for warning TEXT - light
 * `accentHover`, 6.21 here; dark `warning` itself, so the dark pill is unchanged. The tint keeps
 * the tone, so "needs changes" still wears warning.
 */
const TONE_INK: Readonly<Partial<Record<StatusTone, string>>> = {
  warning: '--mx-warning-ink',
};

/** The status's name in the cabinet's language - call it when rendering, never at module load. */
export function statusLabel(status: ArticleStatus): string {
  return i18n.t(`status.${status}.label`, { ns: 'cabinet' });
}

export function StatusPill({ status }: { status: ArticleStatus }) {
  const { t } = useTranslation('cabinet');
  const tone = STATUS_TONE[status];
  const token = TONE_COLOUR[tone];
  const ink = TONE_INK[tone] ?? token;
  const tint = `oklch(var(${token}) / 0.14)`;
  return (
    <Tooltip label={t(`status.${status}.meaning`)} multiline w={280}>
      <Badge
        variant="light"
        radius="sm"
        styles={{
          root: {
            // ★ THE TINT SITS ON AN OPAQUE BASE OF THE PILL'S OWN, so nothing under the pill enters
            // what its ink is read against. A tint alone let a HOVERED table row show through: in
            // dark `danger` fell to 4.07 and `m3` to 3.76 over the hover lift, in light every tone
            // to about 2 over the grey hover fill. The base is the card colour - dark `surface2`,
            // light white - not the page ground, because light measures better on it: `danger`
            // 4.82 against 4.45, `accent` 4.55 against 4.21. Derived, rest and hover alike: dark
            // 4.82-8.64; light 4.55-6.21, `warning` included now its words take the warning INK
            // (`TONE_INK` above). Held by web/authors/test/features/cabinet/status-pill.spec.tsx.
            backgroundColor: 'var(--mantine-color-body)',
            backgroundImage: `linear-gradient(${tint}, ${tint})`,
            color: `oklch(var(${ink}))`,
            cursor: 'help',
          },
        }}
      >
        {t(`status.${status}.label`)}
      </Badge>
    </Tooltip>
  );
}
